import { useIntl } from 'react-intl';
import { z } from 'zod';

import { isEmptyHtmlContent } from '@/utils/string-util';

/**
 * Hook to create the Zod schema for `PoolDetails` — the pool-specific fields
 * stored under `channels.pool.poolDetails`. The campaign date fields
 * (`startsAt` / `endAt`) live at the top level of `ChannelFormState` and are
 * validated by the channel schema.
 */
export function usePoolDetailsSchema() {
  const intl = useIntl();

  const messages = {
    campaignTypeRequired: intl.formatMessage({
      defaultMessage: 'Campaign type is required',
      description: 'Campaign type input required error message',
    }),
    fromEmailRequired: intl.formatMessage({
      defaultMessage: 'Select a "From" email address',
      description: 'From email input required error message',
    }),
    audienceIdRequired: intl.formatMessage({
      defaultMessage: 'Select an audience to send this campaign to',
      description: 'Audience ID input required error message',
    }),
    bodyRequired: intl.formatMessage({
      defaultMessage: 'Add a campaign message to continue',
      description: 'Message body input required error message',
    }),
    subjectRequired: intl.formatMessage({
      defaultMessage: 'Subject is required for email campaigns',
      description: 'Subject input required error message for email campaigns',
    }),
  };

  return z
    .object({
      campaignType: z.enum(['email', 'sms']),
      fromEmail: z.string().min(1, {
        message: messages.fromEmailRequired,
      }),
      isNeverEnding: z.boolean(),
      openingMessages: z.record(
        z.string(),
        z.object({
          audienceId: z.string().nullable(),
          body: z.string().nullable(),
          subject: z.string().nullable(),
        }),
      ),
    })
    .superRefine((data, ctx) => {
      Object.entries(data.openingMessages).forEach(([openingId, message]) => {
        if (!message.audienceId || message.audienceId.trim() === '') {
          ctx.addIssue({
            code: 'custom',
            message: messages.audienceIdRequired,
            path: ['openingMessages', openingId, 'audienceId'],
          });
        }

        if (isEmptyHtmlContent(message.body)) {
          ctx.addIssue({
            code: 'custom',
            message: messages.bodyRequired,
            path: ['openingMessages', openingId, 'body'],
          });
        }

        if (
          data.campaignType === 'email' &&
          (!message.subject || message.subject.trim() === '')
        ) {
          ctx.addIssue({
            code: 'custom',
            message: messages.subjectRequired,
            path: ['openingMessages', openingId, 'subject'],
          });
        }
      });
    });
}
